import { useState, useEffect, useCallback } from 'react';

export interface TablePreferences {
    columnOrder: string[];
    columnWidths: Record<string, number>;
    columnVisibility: Record<string, boolean>;
    sortColumn: string;
    sortOrder: 'asc' | 'desc';
}

interface UseTablePreferencesOptions {
    storageKey: string;
    defaultPreferences: TablePreferences;
}

export const useTablePreferences = ({
    storageKey,
    defaultPreferences,
}: UseTablePreferencesOptions) => {
    const [preferences, setPreferences] = useState<TablePreferences>(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);

                // Merge column visibility - add new columns from defaults that don't exist in stored
                const mergedColumnVisibility = {
                    ...defaultPreferences.columnVisibility,
                    ...parsed.columnVisibility,
                };

                // Merge column order - add new columns from defaults that aren't in stored order
                const storedOrder = parsed.columnOrder || [];
                const newColumns = defaultPreferences.columnOrder.filter(
                    col => !storedOrder.includes(col)
                );
                const mergedColumnOrder = [...storedOrder, ...newColumns];

                // Merge with defaults to ensure all properties exist
                return {
                    ...defaultPreferences,
                    ...parsed,
                    // Ensure nested objects are properly merged
                    columnWidths: {
                        ...defaultPreferences.columnWidths,
                        ...(parsed.columnWidths || {}),
                    },
                    columnVisibility: mergedColumnVisibility,
                    columnOrder: mergedColumnOrder,
                };
            }
        } catch (error) {
            console.error('Failed to load table preferences:', error);
        }
        return defaultPreferences;
    });

    // Save to localStorage whenever preferences change
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(preferences));
        } catch (error) {
            console.error('Failed to save table preferences:', error);
        }
    }, [preferences, storageKey]);

    const updatePreferences = useCallback((updates: Partial<TablePreferences>) => {
        setPreferences(prev => ({
            ...prev,
            ...updates,
            // Handle nested object updates properly
            columnWidths: updates.columnWidths
                ? { ...prev.columnWidths, ...updates.columnWidths }
                : prev.columnWidths,
            columnVisibility: updates.columnVisibility
                ? { ...prev.columnVisibility, ...updates.columnVisibility }
                : prev.columnVisibility,
        }));
    }, []);

    const resetToDefaults = useCallback(() => {
        setPreferences(defaultPreferences);
    }, [defaultPreferences]);

    return {
        preferences,
        updatePreferences,
        resetToDefaults,
    };
};
